// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { useTranslations } from "next-intl";
import { FilterSearch, FilterSelect, FiltersToolbar } from "@chotca/ui";
import { useAdminActivityLog } from "@chotca/api-client";
import { useTableFilters } from "@/lib/use-table-filters";
import { usePagination } from "@/components/data-table";
import {
  ActivityLogTable,
  type ActivityRow,
} from "@/components/activity-log-table";

export function ActivityClient() {
  const t = useTranslations("admin");
  const filters = useTableFilters({
    search: {},
    actorRole: {},
    targetType: {},
  });
  const { page, pageSize, setPage, setPageSize } = usePagination();
  const query = useAdminActivityLog({ page, limit: pageSize });
  const isLoading = query.isLoading;
  const isFetching = query.isFetching;
  const allRows = React.useMemo(
    () => (query.data?.items ?? []) as ActivityRow[],
    [query.data],
  );
  const search = filters.values.search ?? "";
  const actorRole = filters.applied.actorRole ?? "";
  const targetType = filters.applied.targetType ?? "";
  const rows = allRows.filter((r) => {
    if (actorRole && r.actorRole !== actorRole) return false;
    if (targetType && r.targetType !== targetType) return false;
    if (search) {
      const hay = (
        r.action +
        (r.targetType ?? "") +
        (r.actorUserId ?? "")
      ).toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const targetTypeOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const r of allRows) if (r.targetType) set.add(r.targetType);
    return Array.from(set)
      .sort()
      .map((v) => ({ value: v, label: v }));
  }, [allRows]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink">{t("activityTitle")}</h1>
      </header>

      <FiltersToolbar
        hasActiveFilters={filters.hasAny}
        onClearAll={filters.clear}
      >
        <FilterSearch
          value={search}
          onChange={(v) => filters.set("search", v)}
          placeholder="Tìm action / actor / entity..."
        />
        <FilterSelect
          value={filters.values.actorRole ?? ""}
          onValueChange={(v) => filters.set("actorRole", v)}
          placeholder="Mọi vai trò"
          options={[
            { value: "WORKER", label: t("actorRole_WORKER") },
            { value: "EMPLOYER", label: t("actorRole_EMPLOYER") },
            { value: "ADMIN", label: t("actorRole_ADMIN") },
            { value: "SYSTEM", label: t("actorRole_SYSTEM") },
          ]}
        />
        <FilterSelect
          value={filters.values.targetType ?? ""}
          onValueChange={(v) => filters.set("targetType", v)}
          placeholder="Mọi đối tượng"
          options={targetTypeOptions}
        />
      </FiltersToolbar>

      <ActivityLogTable
        rows={rows}
        isLoading={isLoading}
        isFetching={isFetching}
        page={page}
        pageSize={pageSize}
        total={query.data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
